/**
 * Task Notification Mixin
 *
 * Mount on App.vue to enable:
 * - Popup on login showing pending/overdue tasks
 * - Scheduled popups at 8:00 AM, 12:00 PM, 3:00 PM
 * - Continuous polling every 2 minutes for new/updated/cancelled task assignments
 */

var NOTIFICATION_POLL_INTERVAL = 120000; // 2 minutes

export default {
  data() {
    return {
      _taskCheckInterval: null,
      _notificationPollInterval: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.initTaskNotifications();
    });
  },
  beforeDestroy() {
    this.stopAllTaskIntervals();
  },
  methods: {
    initTaskNotifications() {
      var user = this.$store.getters["auth/user"];
      if (!user) return;

      // Gate the whole My Tasks feature (login popup, scheduled popups, polling)
      // on the appbar.reminders permission — so disabling that key (or flipping
      // its global kill-switch) stops the reminder popups from loading at all.
      if (!this.$store.getters["permission/hasPermission"]("appbar.reminders")) {
        this.stopAllTaskIntervals();
        return;
      }

      // Clean up any existing intervals before starting new ones
      this.stopAllTaskIntervals();

      // Set the initial notification check timestamp to now
      // so the first poll only catches changes from this point forward
      if (!this.$store.getters["reminder/last_notification_check"]) {
        this.$store.commit(
          "reminder/SET_LAST_NOTIFICATION_CHECK",
          new Date().toISOString()
        );
      }

      // Fetch pending tasks and show popup on login
      this.fetchAndShowPendingOnLogin();

      // Fetch acknowledged notifications that happened while logged out
      this.$store
        .dispatch("reminder/fetch_acknowledged_on_login")
        .catch((e) => {
          console.warn(
            "[TaskNotification] Failed to fetch acknowledged on login:",
            e
          );
        });

      // Start checking every 60 seconds for scheduled popup times (8AM, 12PM, 3PM)
      this._taskCheckInterval = setInterval(() => {
        this.checkPopupSchedule();
      }, 60000);

      // Start polling every 2 minutes for new/updated task assignment notifications
      this._notificationPollInterval = setInterval(() => {
        this.pollTaskNotifications();
      }, NOTIFICATION_POLL_INTERVAL);
    },

    /**
     * Stop all polling intervals. Called on beforeDestroy and before re-init.
     */
    stopAllTaskIntervals() {
      if (this._taskCheckInterval) {
        clearInterval(this._taskCheckInterval);
        this._taskCheckInterval = null;
      }
      if (this._notificationPollInterval) {
        clearInterval(this._notificationPollInterval);
        this._notificationPollInterval = null;
      }
    },

    /**
     * Clear notification state and stop intervals. Called on logout.
     */
    cleanupTaskNotifications() {
      this.stopAllTaskIntervals();
      this.$store.dispatch("reminder/clear_notification_state");
    },

    async fetchAndShowPendingOnLogin() {
      try {
        await this.$store.dispatch("reminder/get_pending_tasks");
        var count = this.$store.getters["reminder/pending_count"];
        if (count > 0) {
          this.$store.dispatch("reminder/trigger_popup");
        }
      } catch (e) {
        console.warn("[TaskNotification] Failed to fetch pending tasks:", e);
      }
    },

    checkPopupSchedule() {
      var user = this.$store.getters["auth/user"];
      if (!user) return;

      var now = new Date();
      var hours = now.getHours();
      var minutes = now.getMinutes();

      // Schedule: 8:00 AM, 12:00 PM, 3:00 PM
      var schedule = [
        { hour: 8, minute: 0 },
        { hour: 12, minute: 0 },
        { hour: 15, minute: 0 },
      ];

      var matchesSchedule = schedule.some(
        (s) => s.hour === hours && Math.abs(s.minute - minutes) <= 1
      );

      if (!matchesSchedule) return;

      // Prevent double-firing within 5 minutes
      var lastPopup = this.$store.state.reminder.last_popup_time;
      if (lastPopup && Date.now() - lastPopup < 5 * 60 * 1000) return;

      // Refresh and show popup if there are pending tasks
      this.$store.dispatch("reminder/get_pending_tasks").then(() => {
        var count = this.$store.getters["reminder/pending_count"];
        if (count > 0) {
          this.$store.dispatch("reminder/trigger_popup");
        }
      });
    },

    /**
     * Poll the server for new/updated/cancelled task assignment notifications.
     * Called every 2 minutes. Errors are caught silently to prevent
     * notification spam on network issues.
     */
    pollTaskNotifications() {
      var user = this.$store.getters["auth/user"];
      if (!user) return;

      // Do not poll if the notification dialog is already open
      // to avoid overwriting the currently displayed notifications
      if (this.$store.getters["reminder/show_notification_dialog"]) return;

      this.$store.dispatch("reminder/fetch_task_notifications").catch((e) => {
        // Silently ignore network errors -- the next poll will retry
        console.warn("[TaskNotification] Poll failed:", e.message || e);
      });
    },
  },
};
